import React, { useState, useRef } from 'react';
import { 
  Editor, 
  EditorState, 
  RichUtils, 
  convertToRaw, 
  convertFromRaw,
  ContentState,
  Modifier 
} from 'draft-js';
import 'draft-js/dist/Draft.css';
import { Box, IconButton, Divider, Popover, Tooltip } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import SaveIcon from '@mui/icons-material/Save';

const PostEditor = ({ content, onChange, onSave }) => {
  const [editorState, setEditorState] = useState(() => {
    if (content) {
      try {
        // Try to parse as JSON first (for previously edited content)
        const contentState = convertFromRaw(JSON.parse(content));
        return EditorState.createWithContent(contentState);
      } catch (e) {
        // If not JSON, treat as plain text
        const contentState = ContentState.createFromText(content);
        return EditorState.createWithContent(contentState);
      }
    }
    return EditorState.createEmpty();
  });

  // Add emoji picker state
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiButtonRef = useRef(null);

  const handleKeyCommand = (command, editorState) => {
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      handleEditorChange(newState);
      return 'handled';
    }
    return 'not-handled';
  };

  const handleEditorChange = (newState) => {
    setEditorState(newState);
    // Convert content to raw JSON to save
    const contentState = newState.getCurrentContent();
    const raw = convertToRaw(contentState);
    onChange(JSON.stringify(raw));
  };

  const toggleInlineStyle = (style) => {
    handleEditorChange(RichUtils.toggleInlineStyle(editorState, style));
  };

  const toggleBlockType = (blockType) => {
    handleEditorChange(RichUtils.toggleBlockType(editorState, blockType));
  };

  // Add emoji insertion handler
  const insertEmoji = (emoji) => {
    const contentState = editorState.getCurrentContent();
    const contentStateWithEmoji = Modifier.insertText(
      contentState,
      editorState.getSelection(),
      emoji.native
    );
    
    const newEditorState = EditorState.push(
      editorState,
      contentStateWithEmoji,
      'insert-characters'
    );
    
    handleEditorChange(newEditorState);
    setShowEmojiPicker(false);
  };

  return (
    <Box sx={{ 
      border: '1px solid #ddd',
      borderRadius: 1,
      bgcolor: 'white',
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Box sx={{ 
        display: 'flex', 
        gap: 1, 
        p: 1, 
        borderBottom: '1px solid #ddd',
        bgcolor: '#f5f5f5',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton 
            size="small" 
            onClick={() => toggleInlineStyle('BOLD')}
          >
            <FormatBoldIcon fontSize="small" />
          </IconButton>
          <IconButton 
            size="small" 
            onClick={() => toggleInlineStyle('ITALIC')}
          >
            <FormatItalicIcon fontSize="small" />
          </IconButton>
          <Divider orientation="vertical" flexItem />
          <IconButton 
            size="small" 
            onClick={() => toggleBlockType('unordered-list-item')}
          >
            <FormatListBulletedIcon fontSize="small" />
          </IconButton>
          <IconButton 
            size="small" 
            onClick={() => toggleBlockType('ordered-list-item')}
          >
            <FormatListNumberedIcon fontSize="small" />
          </IconButton>
          <Divider orientation="vertical" flexItem />
          <IconButton 
            ref={emojiButtonRef}
            size="small" 
            onClick={() => setShowEmojiPicker(true)}
          >
            <EmojiEmotionsIcon fontSize="small" />
          </IconButton>
        </Box>

        <Tooltip title="Save changes">
          <IconButton 
            size="small" 
            onClick={() => {
              const contentState = editorState.getCurrentContent();
              const raw = convertToRaw(contentState);
              const jsonContent = JSON.stringify(raw);
              onSave && onSave(jsonContent);
            }}
            sx={{
              color: '#4890fe',
              '&:hover': {
                bgcolor: 'rgba(72, 144, 254, 0.1)'
              }
            }}
          >
            <SaveIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Emoji Picker Popover */}
        <Popover
          open={showEmojiPicker}
          anchorEl={emojiButtonRef.current}
          onClose={() => setShowEmojiPicker(false)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          sx={{
            '& .MuiPopover-paper': {
              boxShadow: 3,
              borderRadius: 1
            }
          }}
        >
          <Picker
            data={data}
            onEmojiSelect={insertEmoji}
            theme="light"
            set="native"
          />
        </Popover>
      </Box>

      <Box sx={{ 
        flex: 1,
        overflow: 'auto',
        p: 2,
        '& .DraftEditor-root': {
          height: '100%',
        },
        '& .DraftEditor-editorContainer': {
          height: '100%',
        },
        '& .public-DraftEditor-content': {
          height: '100%',
          minHeight: '200px'
        }
      }}>
        <Editor
          editorState={editorState}
          onChange={handleEditorChange}
          handleKeyCommand={handleKeyCommand}
          placeholder="Write your post here..."
        />
      </Box>
    </Box>
  );
};

export default PostEditor; 